import { useQuery } from '@tanstack/react-query';

import { api } from '../../services/client';
import {
  IUser,
  IUserWithStats,
  IUserDetail,
  IPaginated,
  IDashboardStats,
} from '../../types';
import { createMutationHook } from './createMutation';
import { buildQueryString } from '../../utils/buildQueryString';

// --- Interfaces ---

export interface IUserListParams {
  page: number;
  search: string;
  filter: string;
  limit: number;
}

export interface ICreateUserData {
  username: string;
  displayName: string;
  password: string;
  mobileNumber?: string;
  countryCode?: string;
}

export interface IUpdateUserData {
  displayName: string;
  mobileNumber?: string;
  countryCode?: string;
  password?: string;
}

export interface IBanUserData {
  userId: string;
  reason: string;
}

export interface IMuteUserData {
  userId: string;
  reason: string;
  mutedUntil?: string;
}

// --- Query Key Factory ---

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: IUserListParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

// --- Query Hooks ---

export const useDashboardStats = () =>
  useQuery({
    queryKey: ['dashboard-stats'] as const,
    queryFn: () => api.get<IDashboardStats>('/backoffice/dashboard-stats'),
  });

export const useUsers = (params: IUserListParams) =>
  useQuery({
    queryKey: userKeys.list(params),
    queryFn: () =>
      api.get<IPaginated<IUserWithStats>>(
        `/backoffice/users?${buildQueryString({
          search: params.search || undefined,
          page: params.page,
          limit: params.limit,
          filter:
            params.filter && params.filter !== 'all'
              ? params.filter
              : undefined,
        })}`,
      ),
    placeholderData: (previousData) => previousData,
  });

export const useUserDetail = (id: string) =>
  useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => api.get<IUserDetail>(`/backoffice/users/${id}/detail`),
    enabled: !!id,
  });

// --- Mutation Hooks ---

const DASHBOARD_STATS_KEY = ['dashboard-stats'] as const;

export const useCreateUser = createMutationHook<IUser, ICreateUserData>({
  mutationFn: (data) => api.post<IUser>('/admin/users', data),
  invalidateKeys: [userKeys.lists()],
  delayedInvalidateKeys: [DASHBOARD_STATS_KEY],
  successMessage: 'toast.user_created',
  errorMessage: 'toast.user_create_failed',
});

export const useUpdateUser = createMutationHook<
  IUser,
  { id: string; data: IUpdateUserData }
>({
  mutationFn: ({ id, data }) => api.patch<IUser>(`/admin/users/${id}`, data),
  invalidateKeys: (variables) => [
    userKeys.lists(),
    userKeys.detail(variables.id),
  ],
  successMessage: 'toast.user_updated',
  errorMessage: 'toast.user_update_failed',
});

export const useDeleteUser = createMutationHook<void, string>({
  mutationFn: (id) => api.delete<void>(`/admin/users/${id}`),
  invalidateKeys: [userKeys.lists()],
  delayedInvalidateKeys: [DASHBOARD_STATS_KEY],
  successMessage: 'toast.user_deleted',
  errorMessage: 'toast.user_delete_failed',
});

export const useBanUser = createMutationHook<void, IBanUserData>({
  mutationFn: ({ userId, reason }) =>
    api.post<void>(`/admin/users/${userId}/ban`, { reason }),
  invalidateKeys: (variables) => [
    userKeys.lists(),
    userKeys.detail(variables.userId),
  ],
  delayedInvalidateKeys: [DASHBOARD_STATS_KEY],
  successMessage: 'toast.user_banned',
  errorMessage: 'toast.user_ban_failed',
});

export const useUnbanUser = createMutationHook<void, string>({
  mutationFn: (userId) => api.post<void>(`/admin/users/${userId}/unban`),
  invalidateKeys: (userId) => [userKeys.lists(), userKeys.detail(userId)],
  delayedInvalidateKeys: [DASHBOARD_STATS_KEY],
  successMessage: 'toast.user_unbanned',
  errorMessage: 'toast.user_unban_failed',
});

export const useMuteUser = createMutationHook<void, IMuteUserData>({
  mutationFn: ({ userId, reason, mutedUntil }) => {
    const payload: Record<string, unknown> = { reason };
    if (mutedUntil) payload.mutedUntil = mutedUntil;
    return api.post<void>(`/admin/users/${userId}/mute`, payload);
  },
  invalidateKeys: (variables) => [
    userKeys.lists(),
    userKeys.detail(variables.userId),
  ],
  successMessage: 'toast.user_muted',
  errorMessage: 'toast.user_mute_failed',
});

export const useUnmuteUser = createMutationHook<void, string>({
  mutationFn: (userId) => api.post<void>(`/admin/users/${userId}/unmute`),
  invalidateKeys: (userId) => [userKeys.lists(), userKeys.detail(userId)],
  successMessage: 'toast.user_unmuted',
  errorMessage: 'toast.user_unmute_failed',
});
